// frontend/src/components/Admin/CronogramaAccessManager.js

import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const CronogramaAccessManager = () => {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [diasAcesso, setDiasAcesso] = useState(30); // Valor padrão
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(false);

  useEffect(() => {
    fetchAllowedEmails();
  }, []);

  const fetchAllowedEmails = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/cronograma-access');
      setEmails(response.data.accesses);
    } catch (error) {
      console.error('Erro ao carregar emails permitidos:', error);
      let errorMessage = 'Erro ao carregar emails permitidos';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();

    if (!newEmail) {
      setError('Por favor, insira um email válido');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await api.post('/api/cronograma-access', {
        email: newEmail,
        diasAcesso: parseInt(diasAcesso)
      });
      setSuccess('Email adicionado com sucesso!');
      setNewEmail('');
      setDiasAcesso(30); // Resetar para o valor padrão
      fetchAllowedEmails(); // Refresh the list
    } catch (error) {
      console.error('Erro ao adicionar email:', error);
      let errorMessage = 'Erro ao adicionar email';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEmail = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover o acesso ao cronograma deste usuário?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      await api.delete(`/api/cronograma-access/${id}`);
      setSuccess('Acesso ao cronograma removido com sucesso!');
      fetchAllowedEmails(); // Refresh the list
    } catch (error) {
      console.error('Erro ao remover acesso:', error);
      let errorMessage = 'Erro ao remover acesso';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreEmail = async (id) => {
    if (!window.confirm('Tem certeza que deseja restaurar o acesso ao cronograma para este usuário?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      await api.put(`/api/cronograma-access/${id}/restore`, {});
      setSuccess('Acesso ao cronograma restaurado com sucesso!');
      fetchAllowedEmails(); // Refresh the list
    } catch (error) {
      console.error('Erro ao restaurar acesso:', error);
      let errorMessage = 'Erro ao restaurar acesso';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const filteredEmails = () => {
    if (!showOnlyActive) {
      return emails;
    }
    return emails.filter(email => email.isAllowed);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Gerenciamento de Acesso ao Cronograma</h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Form to add new email */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Adicionar Email Permitido</h3>
        <form onSubmit={handleAddEmail} className="flex flex-col sm:flex-row gap-4">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Digite o email"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <select
            value={diasAcesso}
            onChange={(e) => setDiasAcesso(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={30}>30 dias</option>
            <option value={90}>90 dias</option>
            <option value={180}>180 dias</option>
          </select>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Adicionando...' : 'Adicionar Email'}
          </button>
        </form>
      </div>

      {/* List of allowed emails */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex justify-between items-center p-6 pb-4">
          <h3 className="text-lg font-semibold text-gray-800">Emails Permitidos</h3>
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showOnlyActive}
                onChange={(e) => setShowOnlyActive(e.target.checked)}
                className="mr-2 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Mostrar apenas usuários ativos</span>
            </label>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dias de Acesso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Expiração
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adicionado Por
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Adição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmails().length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      Nenhum email encontrado com os critérios selecionados
                    </td>
                  </tr>
                ) : (
                  filteredEmails().map((access) => (
                    <tr key={access._id} className={access.isAllowed ? '' : 'bg-red-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{access.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            access.isAllowed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {access.isAllowed ? 'Ativo' : 'Removido'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {access.diasAcesso ? `${access.diasAcesso} dias` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(access.dataExpiracao)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {access.addedBy?.nome || access.addedBy?._id || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(access.addedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        {access.isAllowed ? (
                          <button
                            onClick={() => handleRemoveEmail(access._id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Revogar Acesso
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRestoreEmail(access._id)}
                            className="text-green-600 hover:text-green-900 font-medium"
                          >
                            Restaurar Acesso
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CronogramaAccessManager;